import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { DocxExportsFolderReadSchema } from '@/agent-v2/docx-export/schemas'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

/** The Matter Files folder of the matter's exported drafts, or null before the first export is filed. */
export async function getDocxExportsFolder(matterId: string): Promise<string | null> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}/api/v2/matter-artifact/docx-exports/folder?matter_id=${encodeURIComponent(matterId)}`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = (parseError ? {} : data) as { detail?: unknown }
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Get .docx exports folder failed - status: ${response.status}, detail: ${JSON.stringify(errorData.detail)}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, `Failed to get the .docx exports folder: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Get .docx exports folder response JSON parse failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), matterId, parseError: formatUnknownError(parseError) },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid .docx exports folder response JSON')
    }

    const parsed = DocxExportsFolderReadSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error('Get .docx exports folder response schema validation failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), matterId, responseKeys: getTopLevelKeys(data), issues: parsed.error.issues },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid .docx exports folder response shape', parsed.error.issues)
    }

    return parsed.data.folder_id
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
